import { useEffect, useRef, useState } from 'react';
import { useParallax } from '../hooks/useScrollAnimation';
import PhoneMock3D from './PhoneMock3D';
import './Hero.css';

export default function Hero() {
  const heroRef = useRef<HTMLElement>(null);
  const { offset: parallaxOffset } = useParallax(0.3);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const scrollY = window.scrollY;
        const heroBackground = heroRef.current.querySelector('.hero-background') as HTMLElement;
        if (heroBackground && scrollY < window.innerHeight) {
          heroBackground.style.transform = `translateY(${scrollY * 0.5}px)`;
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Используем requestAnimationFrame для оптимизации
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
      
      rafRef.current = requestAnimationFrame(() => {
        if (heroRef.current) {
          const rect = heroRef.current.getBoundingClientRect();
          const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
          const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
          setMousePosition({ x, y });
        }
      });
    };

    const heroEl = heroRef.current;

    window.addEventListener('scroll', handleScroll, { passive: true });
    heroEl?.addEventListener('mousemove', handleMouseMove, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      heroEl?.removeEventListener('mousemove', handleMouseMove);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-background"></div>
      <div className="container">
        <div className="hero-content">
          <div className="hero-text" style={{ transform: `translateY(${parallaxOffset * 0.5}px)` }}>
            <div className="logo-wrapper">
              <h1 className="logo">
                <span className="logo-main">RECVISIT</span>
              </h1>
            </div>
            <p className="hero-tagline">Учет посещаемости на объектах</p>
            <h2 className="hero-title">Контроль посещений через QR-коды</h2>
            <p className="hero-description">
              Сотрудник открывает приложение — планшет считывает код.<br />
              Система фиксирует посещения объекта автоматически.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
              <a href="https://reg.recvi.ru" target="_blank" rel="noopener noreferrer" className="hero-badge" style={{ textDecoration: 'none' }}>
                <span className="badge-icon">🚀</span>
                <span>Попробовать бесплатно</span>
              </a>
              <a href="https://panel.recvi.ru" target="_blank" rel="noopener noreferrer" className="hero-login-link" style={{ textDecoration: 'none', color: 'var(--color-text-light)', fontSize: '0.95rem' }}>
                Уже зарегистрированы? Войти в систему
              </a>
            </div>
          </div>
          
          <div className="hero-demo">
            {/* Декоративные элементы с parallax - каждый на своем плане */}
            <div className="crypto-float crypto-float-1" style={{
              transform: `translateX(${mousePosition.x * -35}px) translateY(${mousePosition.y * -30}px) rotate(-12deg)`
            }}>
              <span className="crypto-icon-small">🔐</span>
              <span className="crypto-text-small">Шифрование</span>
            </div>
            
            <div className="crypto-float crypto-float-2" style={{
              transform: `translateX(${mousePosition.x * 40}px) translateY(${mousePosition.y * -35}px) rotate(8deg)`
            }}>
              <span className="crypto-icon-small">✍️</span>
              <span className="crypto-text-small">Подпись</span>
            </div>
            
            <div className="crypto-float crypto-float-3" style={{
              transform: `translateX(${mousePosition.x * -30}px) translateY(${mousePosition.y * 40}px) rotate(-5deg)`
            }}>
              <span className="crypto-icon-small">#️⃣</span>
              <span className="crypto-text-small">Идентификатор</span>
            </div>
            
            <div className="crypto-float crypto-float-4" style={{
              transform: `translateX(${mousePosition.x * 38}px) translateY(${mousePosition.y * 35}px) rotate(10deg)`
            }}>
              <span className="crypto-icon-small">✓</span>
              <span className="crypto-text-small">Проверка</span>
            </div>

            <div className="parallax-layer" style={{
              transform: `translateX(${mousePosition.x * -8}px) translateY(${mousePosition.y * -8}px)`
            }}>
              <PhoneMock3D />
            </div>
            
            <p className="demo-note" style={{
              transform: `translateX(${mousePosition.x * 5}px) translateY(${mousePosition.y * 5}px)`
            }}>Уникальный QR-код для каждого сотрудника</p>
          </div>
        </div>
      </div>
      <div className="scroll-indicator">
        <div className="scroll-arrow"></div>
      </div>
    </section>
  );
}
